import React, { useState, useEffect, useRef } from 'react';
import { Map, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './MapboxHeatmap.module.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX;

export default function MapboxHeatmap({ data, locationStats }) {
    const [viewState, setViewState] = useState({
        longitude: -95.7129,
        latitude: 37.0902,
        zoom: 3,
        pitch: 0,
        bearing: 0
    });
    
    const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/dark-v11');
    const [showControls, setShowControls] = useState(false);
    
    // Process data for heatmap
    const heatmapData = React.useMemo(() => {
        if (!data || data.length === 0) return null;
        
        const features = data.map(item => {
            const location = item.location;
            if (!location || !location.lat || !location.lng) return null;
            
            return {
                type: 'Feature',
                properties: {
                    count: item.count,
                    ip: item.ip,
                    location: `${location.city}, ${location.state || location.country}`
                },
                geometry: {
                    type: 'Point',
                    coordinates: [location.lng, location.lat]
                }
            };
        }).filter(Boolean);
        
        return {
            type: 'FeatureCollection',
            features: features
        };
    }, [data]);
    
    // Heatmap layer configuration
    const heatmapLayer = {
        id: 'heatmap',
        type: 'heatmap',
        source: 'scans',
        maxzoom: 15,
        paint: {
            // Increase the heatmap weight based on frequency and property magnitude
            'heatmap-weight': [
                'interpolate',
                ['linear'],
                ['get', 'count'],
                0, 0,
                6, 2
            ],
            // Increase the heatmap color weight weight by zoom level
            // heatmap-intensity is a multiplier on top of heatmap-weight
            'heatmap-intensity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0, 1,
                15, 3
            ],
            // Color ramp for heatmap. Domain is 0 (low) to 1 (high).
            'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0, 'rgba(33,102,172,0)',
                0.2, 'rgb(103,169,207)',
                0.4, 'rgb(209,229,240)',
                0.6, 'rgb(253,219,199)',
                0.8, 'rgb(239,138,98)',
                1, 'rgb(178,24,43)'
            ],
            // Adjust the heatmap radius by zoom level
            'heatmap-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0, 2,
                15, 20
            ],
            // Transition from heatmap to circle layer by zoom level
            'heatmap-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                7, 1,
                15, 0
            ]
        }
    };
    
    // Circle layer for individual points at high zoom
    const circleLayer = {
        id: 'scans-point',
        type: 'circle',
        source: 'scans',
        minzoom: 10,
        paint: {
            'circle-radius': [
                'interpolate',
                ['linear'],
                ['get', 'count'],
                1, 4,
                10, 8,
                20, 12
            ],
            'circle-color': [
                'interpolate',
                ['linear'],
                ['get', 'count'],
                1, '#161d2d',
                5, '#374151',
                10, '#6b7280',
                20, '#9ca3af'
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                7, 0,
                15, 1
            ]
        }
    };
    
    const totalScans = locationStats?.scansWithLocation || 0;
    const uniqueLocations = locationStats?.uniqueLocations || 0;
    
    const mapStyles = [
        { id: 'dark-v11', name: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
        { id: 'light-v11', name: 'Light', url: 'mapbox://styles/mapbox/light-v11' },
        { id: 'satellite-v9', name: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9' },
        { id: 'streets-v12', name: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h3 className={styles.title}>
                        <i className="fa-solid fa-globe"></i>
                        Global Scan Heatmap
                    </h3>
                    <p className={styles.subtitle}>Interactive visualization of QR scan locations worldwide</p>
                </div>
                
                <div className={styles.stats}>
                    <div className={styles.stat}>
                        <span className={styles.statValue}>{totalScans}</span>
                        <span className={styles.statLabel}>Total Scans</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statValue}>{uniqueLocations}</span>
                        <span className={styles.statLabel}>Locations</span>
                    </div>
                    {locationStats?.scansWithoutLocation > 0 && (
                        <div className={styles.unknownIndicator}>
                            <i className="fa-solid fa-question-circle"></i>
                            <span>{locationStats.scansWithoutLocation} unknown</span>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.mapWrapper}>
                {/* Map Controls */}
                <div className={styles.mapControls}>
                    <button 
                        className={styles.controlButton}
                        onClick={() => setShowControls(!showControls)}
                        title="Map Settings"
                    >
                        <i className="fa-solid fa-sliders"></i>
                    </button>
                    
                    {showControls && (
                        <div className={styles.controlsPanel}>
                            <div className={styles.controlGroup}>
                                <label className={styles.controlLabel}>Map Style</label>
                                <div className={styles.styleButtons}>
                                    {mapStyles.map(style => (
                                        <button
                                            key={style.id}
                                            className={`${styles.styleButton} ${mapStyle === style.url ? styles.active : ''}`}
                                            onClick={() => setMapStyle(style.url)}
                                        >
                                            {style.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mapbox Map */}
                <div className={styles.mapContainer}>
                    {!MAPBOX_TOKEN ? (
                        <div className={styles.tokenWarning}>
                            <div className={styles.warningContent}>
                                <i className="fa-solid fa-key"></i>
                                <h4>Mapbox Token Required</h4>
                                <p>To display the interactive heatmap, you need a free Mapbox access token.</p>
                                <ol>
                                    <li>Visit <a href="https://account.mapbox.com/" target="_blank" rel="noopener noreferrer">account.mapbox.com</a></li>
                                    <li>Create a free account (50K map loads/month)</li>
                                    <li>Copy your access token</li>
                                    <li>Add REACT_APP_MAPBOX=your_token_here to your .env file</li>
                                </ol>
                            </div>
                        </div>
                    ) : (
                        <Map
                            {...viewState}
                            onMove={evt => setViewState(evt.viewState)}
                            mapboxAccessToken={MAPBOX_TOKEN}
                            style={{ width: '100%', height: '100%' }}
                            mapStyle={mapStyle}
                            interactiveLayerIds={['scans-point']}
                        >
                            {heatmapData && (
                                <Source id="scans" type="geojson" data={heatmapData}>
                                    <Layer {...heatmapLayer} />
                                    <Layer {...circleLayer} />
                                </Source>
                            )}
                        </Map>
                    )}
                </div>

                {/* Legend */}
                <div className={styles.legend}>
                    <div className={styles.legendTitle}>Scan Density</div>
                    <div className={styles.legendGradient}>
                        <div className={styles.gradientBar}></div>
                        <div className={styles.legendLabels}>
                            <span>Low</span>
                            <span>High</span>
                        </div>
                    </div>
                </div>
            </div>

            {(!data || data.length === 0) && (
                <div className={styles.noData}>
                    <i className="fa-solid fa-map-location-dot"></i>
                    <h4>No Location Data Available</h4>
                    <p>Scan data will appear here when location information is available.</p>
                </div>
            )}
        </div>
    );
} 